import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { clientAPI } from '../services/api';

// Default settings (fallback)
const DEFAULT_SETTINGS = {
  features: {
    voice_recordings: true,
    script_builder: true,
    knowledge_base: true,
    real_time_monitoring: true,
    analytics_dashboard: true,
    integrations_marketplace: true,
    inbound_calls: true,
    outbound_calls: true,
    call_transfer: false,
    voice_selection: true,
    custom_greetings: true,
    api_access: false,
    bulk_export: true,
    white_labeling: false
  },
  limits: {
    max_voice_agents: 5,
    max_phone_numbers: 2,
    max_end_users: 10,
    knowledge_base_size_mb: 50,
    max_call_duration_minutes: 30,
    monthly_minutes: 500,
    max_concurrent_calls: 3
  },
  voice_settings: {
    allowed_voices: ['nova', 'alloy', 'echo', 'fable', 'onyx', 'shimmer'],
    default_voice: 'nova',
    pitch_adjustment_enabled: true,
    pitch_range: { min: 0.5, max: 2.0 },
    default_pitch: 1.0,
    speed_adjustment_enabled: true,
    speed_range: { min: 0.5, max: 2.0 },
    default_speed: 1.0
  }
};

const ClientSettingsContext = createContext({
  settings: DEFAULT_SETTINGS,
  usage: null,
  loading: true,
  error: null,
  planName: 'Custom',
  // Feature check helpers
  hasFeature: () => true,
  getLimit: () => 0,
  getVoiceSetting: () => null,
  isVoiceAllowed: () => true,
  canCreateMore: () => true,
  refreshSettings: () => {},
  refreshUsage: () => {}
});

export const useClientSettings = () => useContext(ClientSettingsContext);

export const ClientSettingsProvider = ({ children, clientId }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planName, setPlanName] = useState('Custom');

  const fetchSettings = useCallback(async () => {
    if (!clientId) return;
    
    try {
      const response = await clientAPI.getSettings(clientId);
      
      if (response.data.success) {
        setSettings(response.data.settings);
        setPlanName(response.data.plan_name || 'Custom');
      }
    } catch (err) {
      console.error('Error fetching client settings:', err);
      setError('Failed to load settings');
    }
  }, [clientId]);

  const fetchUsage = useCallback(async () => {
    if (!clientId) return;
    
    try {
      const response = await clientAPI.getUsage(clientId);
      
      if (response.data.success) {
        setUsage(response.data);
      }
    } catch (err) {
      console.error('Error fetching client usage:', err);
    }
  }, [clientId]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSettings(), fetchUsage()]);
      setLoading(false);
    };
    
    if (clientId) {
      loadData();
    }
  }, [clientId, fetchSettings, fetchUsage]);

  // Helper: Check if a feature is enabled
  const hasFeature = useCallback((featureName) => {
    return settings?.features?.[featureName] ?? DEFAULT_SETTINGS.features[featureName] ?? true;
  }, [settings]);

  // Helper: Get a limit value
  const getLimit = useCallback((limitName) => {
    return settings?.limits?.[limitName] ?? DEFAULT_SETTINGS.limits[limitName] ?? 0;
  }, [settings]);

  // Helper: Get a voice setting
  const getVoiceSetting = useCallback((settingName) => {
    return settings?.voice_settings?.[settingName] ?? DEFAULT_SETTINGS.voice_settings[settingName];
  }, [settings]);

  // Helper: Check if a voice is allowed
  const isVoiceAllowed = useCallback((voiceName) => {
    const allowedVoices = settings?.voice_settings?.allowed_voices ?? DEFAULT_SETTINGS.voice_settings.allowed_voices;
    return allowedVoices.includes(voiceName);
  }, [settings]);

  // Helper: Check if user can create more of a resource
  const canCreateMore = useCallback((resourceType) => {
    if (!usage) return true;
    
    const resourceMap = {
      'agents': 'voice_agents',
      'voice_agents': 'voice_agents',
      'users': 'end_users',
      'end_users': 'end_users',
      'phone_numbers': 'phone_numbers'
    };
    
    const usageKey = resourceMap[resourceType];
    if (!usageKey || !usage.usage?.[usageKey]) return true;
    
    const { used, limit } = usage.usage[usageKey];
    return used < limit;
  }, [usage]);

  const value = {
    settings,
    usage,
    loading,
    error,
    planName,
    clientId,
    hasFeature,
    getLimit,
    getVoiceSetting,
    isVoiceAllowed,
    canCreateMore,
    refreshSettings: fetchSettings,
    refreshUsage: fetchUsage
  };

  return (
    <ClientSettingsContext.Provider value={value}>
      {children}
    </ClientSettingsContext.Provider>
  );
};

export default ClientSettingsContext;
